import React, { useEffect, useRef, useState } from 'react';

/**
 * MermaidBlock — renders a Mermaid diagram from the text of a fenced
 * code block (```mermaid ... ```).
 *
 *  - Lazy-loads the mermaid library only on first use, so unused folios
 *    don't pay the ~400 KB bundle cost.
 *  - Picks the dark vs light Mermaid theme from the document's current
 *    data-theme attribute, so diagrams match the KDS / Octonius theme.
 *  - Renders into a sandboxed inner div via dangerouslySetInnerHTML
 *    because mermaid.render() returns an SVG string.
 *  - On syntax error, shows an error pane with the message and the
 *    source so the author can fix it inline.
 */

let mermaidPromise = null;
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(mod => mod.default || mod);
  }
  return mermaidPromise;
}

let counter = 0;

function pickMermaidTheme() {
  const t = (document.documentElement.getAttribute('data-theme') || '').toLowerCase();
  if (t.includes('dark') || t === 'dracula') return 'dark';
  return 'default';
}

function MermaidBlock({ chart }) {
  const [svg, setSvg]     = useState('');
  const [error, setError] = useState('');
  const idRef             = useRef(`mermaid-${++counter}`);

  useEffect(() => {
    let cancelled = false;
    setError('');
    setSvg('');

    if (!chart || !chart.trim()) return;

    loadMermaid()
      .then(mermaid => {
        try {
          mermaid.initialize({
            startOnLoad: false,
            theme: pickMermaidTheme(),
            securityLevel: 'strict',
            fontFamily: 'inherit',
          });
        } catch {/* re-init safe */}

        // Mermaid expects each render call to use a unique node id
        const id = `${idRef.current}-${Date.now()}`;
        return mermaid.render(id, chart);
      })
      .then(result => {
        if (cancelled || !result) return;
        setSvg(result.svg || '');
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.message || String(err));
      });

    return () => { cancelled = true; };
  }, [chart]);

  if (error) {
    return (
      <div className="mermaid-block mermaid-block-error" role="alert">
        <div className="mermaid-error-label">Mermaid syntax error</div>
        <div className="mermaid-error-message">{error}</div>
        <pre className="mermaid-error-source">{chart}</pre>
      </div>
    );
  }

  if (!svg) {
    return <div className="mermaid-block mermaid-block-loading">Rendering diagram…</div>;
  }

  return (
    <div
      className="mermaid-block"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export default MermaidBlock;
